import React, { useEffect, useState } from 'react';
import {
  DEFAULT_MEDIA_ASPECT_RATIO,
  mediaFitVars,
  usesIntrinsicContainLayout,
} from './mediaFit';
import { useResolvedRatio } from './useMediaAspectRatio';
import {
  isMediaPresent,
  MEDIA_KINDS,
  normalizeMediaSource,
} from './mediaSource';
import { ImageFrame } from './ImageFrame';
import { VideoFrame } from './VideoFrame';
import classes from './MediaFrame.module.css';
import { resolveTextBackgroundColor } from '../utils/textBackgroundStyle';

/**
 * Média pleine largeur + overlay optionnel + contenu superposé.
 * Layout uniquement : le rendu média vit dans ImageFrame / VideoFrame
 * (dispatch par kind — la donnée `src` en base n'est pas typée).
 */
export function MediaStack({
  imageUrl,
  media,
  alt = '',
  mediaFit = 'cover',
  mediaAspectRatio = 'auto',
  mediaType,
  overlay = null,
  children = null,
  className = '',
  contentClassName = '',
  isEditing = false,
  stopOnUnmount = false,
  asBackground = null,
  /** Login / conteneur hauteur fixe — remplit le parent sans aspectSizer. */
  fillContainer = false,
  /** Fond derrière le média (vide = transparent si défini, sinon CSS par défaut). */
  backgroundColor = undefined,
}) {
  const mediaInput = media ?? imageUrl;
  let resolved = normalizeMediaSource(mediaInput);
  if (mediaType === 'video' && resolved.kind === MEDIA_KINDS.image && resolved.src) {
    resolved = { ...resolved, kind: MEDIA_KINDS.video };
  }

  // Image ou vidéo fichier cassée : le stack entier disparaît au viewer (pas
  // de bande noire vide) — même règle C5 que les blocs. Reset si la source
  // change. Embeds non couverts (l'iframe ne signale pas ses erreurs).
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [resolved.src]);

  const ratio = useResolvedRatio(mediaInput, mediaAspectRatio, DEFAULT_MEDIA_ASPECT_RATIO);
  const fitVars = mediaFitVars(mediaFit);
  const stackBgStyle = backgroundColor !== undefined
    ? { backgroundColor: resolveTextBackgroundColor(backgroundColor) }
    : {};

  const mediaHidden = broken && !isEditing && resolved.kind !== MEDIA_KINDS.embed;
  if (!isMediaPresent(resolved) || mediaHidden) {
    return children ? <div className={className}>{children}</div> : null;
  }

  const stackAsBackground = asBackground === true;
  const intrinsicContain = usesIntrinsicContainLayout(mediaFit, mediaAspectRatio, {
    asBackground: stackAsBackground,
  });

  const isImage = resolved.kind === MEDIA_KINDS.image;
  const mediaEl = isImage
    ? (
      <ImageFrame
        src={resolved.src}
        srcSet={resolved.srcset}
        alt={alt}
        mediaFit={mediaFit}
        mediaFocalPoint={resolved.focalPoint}
        mediaAspectRatio={mediaAspectRatio}
        isEditing={isEditing}
        asBackground={stackAsBackground}
        onError={() => setBroken(true)}
      />
    )
    : (
      <VideoFrame
          stopOnUnmount={stopOnUnmount}
        src={resolved.src}
        alt={alt}
        mediaFit={mediaFit}
        mediaAspectRatio={mediaAspectRatio}
        isEditing={isEditing}
        asBackground={stackAsBackground}
        playback={resolved.playback}
        poster={resolved.poster}
        onError={() => setBroken(true)}
      />
    );

  if (stackAsBackground) {
    const stackFill = fillContainer === true;
    return (
      <div
        className={`${classes.stack} ${classes.stackBackground} ${stackFill ? classes.stackFillContainer : ''} ${className}`.trim()}
        style={stackFill ? { ...fitVars, ...stackBgStyle } : { ...fitVars, ...stackBgStyle, '--media-aspect-ratio': ratio }}
      >
        {!stackFill && <div className={classes.aspectSizer} aria-hidden="true" />}
        <div className={classes.backgroundMedia} style={fitVars}>
          {mediaEl}
        </div>
        {overlay ? <div className={classes.overlaySlot}>{overlay}</div> : null}
        {children ? (
          <div className={`${classes.content} ${contentClassName}`.trim()}>
            {children}
          </div>
        ) : null}
      </div>
    );
  }

  if (intrinsicContain) {
    return (
      <div className={`${classes.stack} ${classes.stackIntrinsic} ${className}`.trim()} style={stackBgStyle}>
        <div className={classes.intrinsicMedia}>{mediaEl}</div>
        {overlay ? <div className={classes.overlaySlot}>{overlay}</div> : null}
        {children ? (
          <div className={`${classes.content} ${contentClassName}`.trim()}>
            {children}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`${classes.stack} ${className}`.trim()} style={{ ...fitVars, ...stackBgStyle }}>
      <div className={classes.aspect}>{mediaEl}</div>
      {overlay ? <div className={classes.overlaySlot}>{overlay}</div> : null}
      {children ? (
        <div className={`${classes.content} ${contentClassName}`.trim()}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
